import { App, Button, Dropdown, Space, Spin } from "antd";
import { FaChevronDown } from "react-icons/fa";
import SubmitActions from "./SubmitActions";
import { StyledTable, StyledValue } from "../../analysisAgent.style";
import { getPercentage, onInitializeDropdownWidth } from "../../../../../utils";
import React, { useEffect, useMemo, useState } from "react";
import { useCommonPropertiesContext } from "../../../../../contexts/commonPropertiesContext";
import type { DataFromAnotherBatchType } from "./types/DataFromAnotherBatchType";
import { useFetchData as usePagination } from "../../../../../hooks/useFetch/pagination";
import DropdownExamplesVariants from "./DropdownExamplesVariants";
import { SpinWrapper } from "../../../../../app/app.style";
import { StyledDataContainer } from "./styles/dataFromAnotherBatch.style";
import type { AttrStatDataType } from "../../UseFetchType";
import SourceTag from "../../../../masterComponents/SourceTag";
import { LoadingOutlined } from "@ant-design/icons";
import type { TableRowSelection } from "antd/es/table/interface";
import { usePayloadCheckboxesActionsContext } from "../../_payloadCheckboxesActionsContext";

const DataFromAnotherBatch: React.FC<DataFromAnotherBatchType> = ({
  attrToAnalyze,
  deferredActionsData,
  setDeferredActionsData,
  curBatch,
  setBatch,
  availableBatches,
  parentRowExamplesParams,
  setParentRowExamplesParams,
  parentRowSourceValuesParams,
  setParentRowSourceValuesParams,
  parentRowStatChildHash,
  parentFrequency,
  parentRowOverlay,
  checkboxesLockRootParentHash,
  renderExpanded,
  isFinalLevel,
  setActiveBatch,
  isLoadingAttrStatChildSubmit,
  isLoadingAttrStatChildValidation,
  fetchAttributeStatistics
}) => {
  const { message: messageApi } = App.useApp();
  const {
    setPendingBtn,
    selectedChildCheckboxKeys,
    setSelectedChildCheckboxKeys,
    selectedParentCheckboxKeys,
    setSelectedParentCheckboxKeys,
    setDataRowForActions,
    dataRowForActions,
  } = usePayloadCheckboxesActionsContext();

  const childCheckboxesLocked =
    checkboxesLockRootParentHash != null &&
    selectedParentCheckboxKeys.some((k) => k === checkboxesLockRootParentHash);

  const { dropdownWithBatchRef } = useCommonPropertiesContext();

  const [totalCount, setTotalCount] = useState(null);

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (!isLoadingAttrStatChildSubmit || isLoadingAttrStatChildValidation) {
      setPendingBtn(null);
    }
  }, [isLoadingAttrStatChildSubmit, isLoadingAttrStatChildValidation]);

  useEffect(() => {
    if (!curBatch && availableBatches.length > 0) {
      setBatch(availableBatches[0].key);
    }
  }, [availableBatches, curBatch, setBatch]);

  const [
    fetchAttrStatChild,
    {
      isLoading: isLoadingAttrStatChildData,
      data: attrStatChildData,
      page: attrStatChildPage,
      totalRows: attrStatChildTotalRows,
      error: attrStatChildError,
      loadMore: attrStatChildLoadMore,
      clearError: clearAttrStatChildError,
    },
  ] = usePagination<AttrStatDataType[]>({
    optionalArray: [curBatch, parentRowStatChildHash],
    query: "/@batch/statistics/child/attributes/@attributeName",
    selectData: (data) => {
      setTotalCount(data.total_count);
      return data.items.map((data) => ({
        verifiedPercentage: data.sources.finals
          ? getPercentage(data.sources.finals, data.frequency)
          : 0,
        hash: data.hash,
        value: data.value,
        frequency: data.frequency,
        sources: Object.keys(data.sources).map((key) => ({
          source: key,
          count: data.sources[key] ?? 0,
          percentage: getPercentage(data.sources[key] ?? 0, data.frequency),
        })),
      }));
    },
    selectPageInfo: (data) => data.page_info,
  });

  const curHash = useMemo(
    () =>
      attrStatChildData?.find((r: { hash: string }) =>
        deferredActionsData.some((item) => item.hash === r.hash),
      )?.hash ?? "",
    [attrStatChildData, deferredActionsData],
  );

  const curHashForCheckbox = useMemo(
    () =>
      attrStatChildData?.find((r: { hash: string }) =>
        dataRowForActions.some((item) => item.hash === r.hash),
      )?.hash ?? "",
    [attrStatChildData, dataRowForActions],
  );

  useEffect(() => {
    if (attrStatChildError) {
      messageApi.error(attrStatChildError.message);
      clearAttrStatChildError?.();
    }
  }, [attrStatChildError, messageApi]);

  useEffect(() => {
    setExpandedKeys([]);
    if (parentRowStatChildHash && curBatch) {
      fetchAttrStatChild({
        batch: curBatch,
        attributeName: attrToAnalyze,
        hash: parentRowStatChildHash,
      });
    }
  }, [curBatch, parentRowStatChildHash]);

  const childHashesOnPage = useMemo(
    () => (attrStatChildData ?? []).map((r) => r.hash),
    [attrStatChildData],
  );

  const effectiveChildKeys = useMemo(() => {
    const keys = selectedChildCheckboxKeys.map(String);
    const root = checkboxesLockRootParentHash;
    if (
      root != null &&
      childHashesOnPage.some((h) => h === root) &&
      selectedParentCheckboxKeys.some((k) => k === root)
    ) {
      const rs = root;
      if (!keys.some((k) => k === rs)) keys.push(rs);
    }
    return keys;
  }, [
    selectedChildCheckboxKeys,
    selectedParentCheckboxKeys,
    childHashesOnPage,
    checkboxesLockRootParentHash,
  ]);

  const { locksChildBatchControls, nestedAllDisabled } = useMemo(() => {
    const root = checkboxesLockRootParentHash;
    const stat = parentRowStatChildHash;
    const isRootChild = root != null && stat === root;

    const nestedAll =
      !isRootChild &&
      (Boolean(
        root &&
        selectedParentCheckboxKeys.some((k) => k === root),
      ) ||
        selectedChildCheckboxKeys.some((k) => k === String(stat)) ||
        dataRowForActions.some(
          (row) =>
            String(row.hash) === String(stat) ||
            (root != null && String(row.hash) === root),
        ));

    const locksChildBatchControls =
      parentRowOverlay ||
      deferredActionsData.some((item) => item.hash === curHash) ||
      dataRowForActions.some((item) => item.hash === curHashForCheckbox) ||
      (isRootChild &&
        root != null &&
        selectedParentCheckboxKeys.some((k) => k === root)) ||
      nestedAll;

    return { locksChildBatchControls, nestedAllDisabled: nestedAll };
  }, [
    parentRowOverlay,
    deferredActionsData,
    curHash,
    curHashForCheckbox,
    dataRowForActions,
    checkboxesLockRootParentHash,
    parentRowStatChildHash,
    selectedParentCheckboxKeys,
    selectedChildCheckboxKeys,
  ]);

  const displayedChildSelectionKeys = useMemo(() => {
    if (childCheckboxesLocked || nestedAllDisabled) {
      return childHashesOnPage.map((h) => String(h));
    }
    return effectiveChildKeys;
  }, [
    childCheckboxesLocked,
    nestedAllDisabled,
    childHashesOnPage,
    effectiveChildKeys,
  ]);

  const childBatchDataColumns = [
    {
      title: "State",
      dataIndex: "verifiedPercentage",
      render: (_: unknown, record: AttrStatDataType) => {
        return (
          <Space direction="vertical">
            {record.sources
              .slice()
              .sort((a, b) => a.source.localeCompare(b.source))
              .map((v) => (
                <SourceTag source={v.source} />
              ))}
          </Space>
        );
      },
    },
    {
      title: "Characteristic Value",
      dataIndex: "value",
      render: (value) => <StyledValue>{value}</StyledValue>,
    },
    {
      title: "Count",
      dataIndex: "frequency",
      render: (frequency: number, record: AttrStatDataType) => (
        <span
          style={{ height: 0 }}
          title={
            record.sources?.length
              ? record.sources
                  .map(
                    (obj) =>
                      `${obj.source === "<empty>" ? "N/A" : obj.source}: ${obj.count} (${obj.percentage}%)`,
                  )
                  .join("\n")
              : ""
          }
        >
          {/* {frequency[0]} ({frequency[1] > 1 ? frequency[1] : `<1`}%) */}
          {frequency}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (_: any, record: AttrStatDataType) => (
        <Space>
          <SubmitActions
            hash={record.hash}
            value={record.value}
            setDeferredActionsData={setDeferredActionsData}
            fetchAttributeStatistics={fetchAttributeStatistics}
            state={record.sources.map((s) => s.source)[0]}
          />
          <DropdownExamplesVariants
            getExamples={() => {
              setParentRowExamplesParams({
                hash: record.hash,
                targetTable: `child-${record.value}`,
              });
              setActiveBatch(curBatch);
            }}
            isFinalLevel={isFinalLevel}
            onSelectHash={() => {
              setExpandedKeys([record.hash]);
            }}
            reviewSourceValues={() => {
              setParentRowSourceValuesParams({
                hash: record.hash,
                targetTable: `child-${record.value}`,
                state: record.sources.map((s) => s.source)[0],
              });
              setActiveBatch(curBatch);
            }}
          />
        </Space>
      ),
    },
  ];

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys: displayedChildSelectionKeys,
    hideSelectAll: true,
    getCheckboxProps: (record: AttrStatDataType) => {
      if (childCheckboxesLocked) return { disabled: true };
      if (nestedAllDisabled) return { disabled: true };
      const root = checkboxesLockRootParentHash;
      if (
        root != null &&
       record.hash === root &&
        selectedParentCheckboxKeys.some((k) => k === root)
      ) {
        return { disabled: true };
      }
      if (
        parentRowStatChildHash != null &&
        record.hash === parentRowStatChildHash &&
        selectedChildCheckboxKeys.some(
          (k) => k === parentRowStatChildHash,
        )
      ) {
        return { disabled: true };
      }
      return { disabled: false };
    },
    onChange: (newKeys) => {
      if (childCheckboxesLocked || nestedAllDisabled) return;
      let keysInThisTable = newKeys.filter((k) =>
        childHashesOnPage.some((h) => h === k),
      );
      const root = checkboxesLockRootParentHash;
      let promotedRootFromChild = false;
      if (
        root != null &&
        childHashesOnPage.some((h) => h === root) &&
        keysInThisTable.some((k) => k === root)
      ) {
        promotedRootFromChild = true;
        setSelectedParentCheckboxKeys((prev) =>
          prev.some((k) => k === root) ? prev : [...prev, root],
        );
        keysInThisTable = keysInThisTable.filter(
          (k) => k !== root,
        );
      }
      setSelectedChildCheckboxKeys((prev) => {
        const outsideThisTable = prev.filter(
          (k) => !childHashesOnPage.some((h) => h === k),
        );
        return [...outsideThisTable, ...keysInThisTable];
      });
      setDataRowForActions((prev) => {
        const withoutThisChildPage = prev.filter(
          (row) =>
            !(attrStatChildData ?? []).some(
              (r) => String(r.hash) === String(row.hash),
            ),
        );
        let fromChild = (attrStatChildData ?? [])
          .filter((r) =>
            keysInThisTable.some((k) => k === String(r.hash)),
          )
          .map((r) => ({
            hash: r.hash,
            target_value: r.value,
            source_value: r.value,
            tag: (r.sources ?? []).flatMap((s) => s.source)[0] ?? "",
            actionSource: "child" as const,
          }));
        if (promotedRootFromChild && root != null) {
          const rootRecord = (attrStatChildData ?? []).find(
            (r) => String(r.hash) === root,
          );
          if (
            rootRecord &&
            !fromChild.some((row) => String(row.hash) === root)
          ) {
            fromChild = [
              ...fromChild,
              {
                hash: rootRecord.hash,
                target_value: rootRecord.value,
                source_value: rootRecord.value,
                tag:
                  (rootRecord.sources ?? []).flatMap((s) => s.source)[0] ?? "",
                actionSource: "child" as const,
              },
            ];
          }
        }
        return [...withoutThisChildPage, ...fromChild];
      });
    },
  };

  return (
    <>
      {isLoadingAttrStatChildData ? (
        <SpinWrapper style={{ justifyContent: "center" }}>
          <Spin indicator={<LoadingOutlined />} size="large" />
        </SpinWrapper>
      ) : (
        <StyledDataContainer>
          <Dropdown
            overlayStyle={{ zIndex: 900 }}
            menu={{
              items: availableBatches,
              onClick: (info) => setBatch(info.key),
              selectedKeys: [curBatch],
            }}
            disabled={locksChildBatchControls}
            trigger={["click"]}
          >
            <Button
              ref={(el) => onInitializeDropdownWidth(el, dropdownWithBatchRef)}
              icon={<FaChevronDown />}
              className="btn-color"
              style={{
                marginRight: "var(--spacing-xs)",
                justifyContent: "flex-start",
              }}
            >
              {curBatch}
            </Button>
          </Dropdown>
          <StyledTable
            rowKey="hash"
            tableLayout="auto"
            className="primary-table child-table"
            columns={childBatchDataColumns}
            dataSource={attrStatChildData ?? []}
            rowSelection={rowSelection}
            rowClassName={(record: AttrStatDataType) => {
              const classes: string[] = [];

              if (
                record.hash === parentRowExamplesParams.hash &&
                parentRowExamplesParams.targetTable === `child-${record.value}`
              ) {
                classes.push("highlight-row");
              }

              if (
                record.hash === parentRowSourceValuesParams.hash &&
                parentRowSourceValuesParams.targetTable ===
                  `child-${record.value}`
              ) {
                classes.push("values-from-hash");
              }

              if (
                deferredActionsData.some((item) => item.hash === record.hash) ||
                parentRowOverlay
              ) {
                classes.push("row-overlay");
              }

              if (record.hash === expandedKeys[0]) {
                classes.push("highlight-row-selected-2");
              }

              return classes.join(" ");
            }}
            pagination={
              attrStatChildData?.length && {
                disabled: locksChildBatchControls,
                current: attrStatChildPage,
                total: attrStatChildTotalRows,
                showSizeChanger: false,
                showQuickJumper: true,
                pageSize: 15,
                showTotal: () => (
                  <span>
                    Count of missed tags: {parentFrequency - totalCount}
                  </span>
                ),
              }
            }
            onChange={(pagination) => {
              attrStatChildLoadMore(pagination.current);
            }}
            expandable={
              renderExpanded && {
                expandedRowKeys: expandedKeys,
                expandIcon: () => null,
                expandedRowClassName: (record: AttrStatDataType) =>
                  record.hash === expandedKeys[0]
                    ? "highlight-row-selected-2"
                    : "",
                expandedRowRender: (record: AttrStatDataType) => {
                  if (record.hash !== expandedKeys[0]) return null;
                  return renderExpanded(record);
                },
              }
            }
          />
        </StyledDataContainer>
      )}
    </>
  );
};

export default DataFromAnotherBatch;
